import {
  useGetInOfficeFamilyMembersQuery,
  useLazyGetInOfficeEventsOfPatientQuery,
} from "@services/formAssignments";
import { IInOfficeEvent } from "../../../types/formAssignments/IInOfficeEvent";
import { useEffect, useState } from "react";
import { useGetFeatureFlag } from "@hooks/useGetFeatureFlag";
import { EFeatureFlags } from "../../../enums/EFeatureFlags";

export type InOfficeEventsPerPatient = Record<string, IInOfficeEvent[]>;

interface UseInitAllInOfficeEventsProps {
  initialInOfficeEvent: IInOfficeEvent;
  onComplete: (allInOfficeEvents: InOfficeEventsPerPatient) => void;
}

export default function useInitAllInOfficeEvents({
  initialInOfficeEvent,
  onComplete,
}: UseInitAllInOfficeEventsProps) {
  const [allInOfficeEvents, setAllInOfficeEvents] =
    useState<InOfficeEventsPerPatient>({
      [initialInOfficeEvent.patient.id]: [initialInOfficeEvent],
    });

  const [isLoading, setIsLoading] = useState(true);

  const { isEnabled: isFamilyFeatureEnabled } = useGetFeatureFlag(
    EFeatureFlags.FastrAppFamilyEnabled
  );

  const { data: familyMembers, isLoading: isLoadingFamilyMembers } =
    useGetInOfficeFamilyMembersQuery(initialInOfficeEvent.patient.id, {
      skip: !isFamilyFeatureEnabled,
    });

  const [getInOfficePatient] = useLazyGetInOfficeEventsOfPatientQuery();

  useEffect(() => {
    if (isLoadingFamilyMembers || familyMembers == null) {
      return;
    }

    const fetchPatients = async () => {
      try {
        const events = (
          await Promise.all(
            [
              // The patient for initial event could have additional appointments
              // for the day that are needed in addition to the family events.
              initialInOfficeEvent.patient,
              ...familyMembers,
            ].map(async (patient) => getInOfficePatient(patient.id))
          )
        ).flatMap(({ data }) => data?.data ?? []);

        const eventsPerPatient = events.reduce((acc, event) => {
          const patientId = event.patient.id;
          if (!acc[patientId]) {
            acc[patientId] = [];
          }
          acc[patientId].push(event);
          return acc;
        }, {} as InOfficeEventsPerPatient);

        // sorting the events for each patient by check in time
        // in case the backend didn't already do so.
        Object.keys(eventsPerPatient).forEach((patientId) => {
          eventsPerPatient[patientId].sort(
            (a, b) => a.checkInTime - b.checkInTime
          );
        });

        setAllInOfficeEvents(eventsPerPatient);
        onComplete(eventsPerPatient);
      } catch (error) {
        console.error("Error fetching in office events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatients();
  }, [
    familyMembers,
    getInOfficePatient,
    initialInOfficeEvent.patient,
    isLoadingFamilyMembers,
    onComplete,
  ]);

  // Delete this when removing isFamilyFeatureEnabled
  useEffect(() => {
    if (!isFamilyFeatureEnabled) {
      setIsLoading(false);
    }
  }, [isFamilyFeatureEnabled]);

  return {
    allInOfficeEvents,
    isLoading,
  };
}
